import type {HeaderData, HeaderNavItem, HeaderNavLink} from '@commercial-website/data-layer'

import type {HeaderQuery} from '@/graphql-types'

type RawHeaderV2 = HeaderQuery['allPrismicHeaderV2']['nodes'][number]['data']
type RawHeaderV2Item = RawHeaderV2['items'][number]
// Prismic only ever sets target to '_blank' for "open in new tab" links — everything else
// (including empty string) means default/same-tab behaviour.
const mapTarget = (target: string | null | undefined): HeaderNavLink['target'] =>
    target === '_blank' ? '_blank' : undefined

// Prismic's `hide_on` is a free-form string in the generated GraphQL types (backed by a select
// field), so guard against unexpected/empty values leaking past the UI's exhaustive mapping.
const VALID_HIDE_ON_VALUES: ReadonlyArray<HeaderNavItem['hideOn']> = [
    'never',
    'mobile',
    'tablet',
    'laptop',
    'all',
]
const mapHideOn = (hideOn: string | null | undefined): HeaderNavItem['hideOn'] =>
    VALID_HIDE_ON_VALUES.includes(hideOn as HeaderNavItem['hideOn'])
        ? (hideOn as HeaderNavItem['hideOn'])
        : 'never'

// Treat an empty string url the same as no url — an empty href would render a dead anchor.
const mapItem = (item: RawHeaderV2Item): HeaderNavItem => {
    const groups = item.dropdown?.document?.data?.body
        ?.map(({primary, items}) => ({
            label: primary.label,
            links: items.flatMap((link) =>
                link.link.url
                    ? [
                          {
                              text: link.text,
                              subText: link.sub_text ?? undefined,
                              href: link.link.url,
                              target: mapTarget(link.link.target),
                          },
                      ]
                    : [],
            ),
        }))
        .filter((group) => group.links.length > 0)

    return {
        label: item.label,
        href: item.link.url || undefined,
        target: mapTarget(item.link.target),
        hideOn: mapHideOn(item.hide_on),
        groups: groups && groups.length > 0 ? groups : undefined,
    }
}

export const mapPrismicHeaderV2 = (raw: RawHeaderV2): HeaderData => ({
    logInText: raw.logInText,
    // Gatsby builds the CTA button's text separately (buildCtaButton, sourced from the legacy
    // Header Prismic doc's start_free_trial field) — HeaderV2's Prismic content has no equivalent
    // field, and this value is never read by header-v2.tsx.
    ctaButtonText: '',
    noResultsText: raw.noResultsText,
    secondaryCtaText: raw.secondaryCtaText || undefined,
    secondaryCtaHref: raw.secondaryCtaLink?.url || undefined,
    items: raw.items.map(mapItem),
})
